<template>
  <section class="transaction-table-wrapper" id="wallet-transaction-table">
    <div class="table-container">
      <table class="transaction-table">
        <thead class="transaction-table__head" v-if="tableRows.length !== 0">
          <tr>
            <th
              class="transaction-table__header-row"
              ref="tableHead"
              :style="gridStyling"
            >
              <div
                v-for="(header, index) in tableHeaders"
                :key="'header' + index"
                class="transaction-table__header-cell"
              >
                {{ header }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="transaction-table__body">
          <tr>
            <td ref="tableRow">
              <div
                class="transaction-table__row"
                v-for="(details, index) in tableRows"
                :key="'row' + index"
                :style="gridStyling"
                @click="viewTransaction(details)"
              >
                <span class="transaction-table__icon">
                  <template v-if="details?.type">
                    <div
                      v-if="
                        details?.type.includes('Mint') ||
                        details?.type.includes('Fund') ||
                        details?.type.includes('Carry') ||
                        details?.type.includes('Buy') ||
                        details?.type.includes('Trade Reversal')
                      "
                      class="transaction-table__icon-box transaction-table__icon-box--credit"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 12.6667V3.33337" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M3.33334 8.00004L8 3.33337L12.6667 8.00004" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <div
                      v-if="
                        details?.type.includes('Withdraw') ||
                        details?.type.includes('Debit Wallet') ||
                        details?.type.includes('Send') ||
                        details?.type.includes('Fee') ||
                        details?.type.includes('Sell') ||
                        details?.type.includes('Plan')
                      "
                      class="transaction-table__icon-box transaction-table__icon-box--debit"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 3.33337V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M12.6667 8.00004L8 12.6667L3.33334 8.00004" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </template>
                  <div v-else class="transaction-table__icon-box transaction-table__icon-box--default">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 3.33337V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M12.6667 8.00004L8 12.6667L3.33334 8.00004" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </span>
                <template
                  v-for="(detail, key, colIndex) in details"
                  :key="'column' + colIndex"
                >
                  <div
                    v-if="key !== 'type' && key !== 'image'"
                    class="transaction-table__cell"
                  >
                    <p
                      v-html="detail"
                      :class="[
                        key === 'status' ? 'status-badge status-badge--' + String(detail).toLowerCase() : null,
                      ]"
                      class="transaction-table__cell-text"
                    ></p>
                  </div>
                </template>
              </div>
              <EmptyState v-if="tableRows.length === 0 && !loading">
                <template v-slot:message>
                  <p class="message tw-text-center tw-text-gray tw-my-3">
                    There has been no transaction on your account.
                  </p>
                </template>
              </EmptyState>
              <SmallLoader v-if="loading && table === 'walletTransactions'" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="pagination-wrapper" v-if="tableRows.length !== 0">
      <Pagination
        :totalPage="pages"
        :currentPage="page"
        :maxVisible="5"
        @pageChange="gotoPage"
      />
    </div>
    <ViewTransactionModal
      v-if="showViewTransactionModal"
      class="tw-z-20"
      @close="showViewTransactionModal = !showViewTransactionModal"
    >
      <template v-slot:content>
        <div class="modal-header">
          <h3 class="modal-header__title">Transaction Summary</h3>
          <p class="modal-header__ref">{{ transactionRef }}</p>
        </div>
        <div class="modal-divider"></div>
        <div class="modal-row">
          <p class="modal-row__value modal-row__value--with-icon">
            {{ description }}
            <img :src="image" class="tw-w-5 tw-h-5" alt="token" />
          </p>
        </div>
        <div class="modal-row">
          <div class="modal-row__item">
            <p class="modal-row__label">Amount</p>
            <p class="modal-row__value">{{ amount }}</p>
          </div>
          <div class="modal-row__item modal-row__item--right">
            <p class="modal-row__label">Type</p>
            <p class="modal-row__value">{{ type }}</p>
          </div>
        </div>
        <div class="modal-row">
          <div class="modal-row__item">
            <p class="modal-row__label">Date</p>
            <p class="modal-row__value">{{ date }}</p>
          </div>
          <div class="modal-row__item modal-row__item--right">
            <p class="modal-row__label">Status</p>
            <p
              class="modal-row__value"
              :class="{
                'modal-row__value--success': status === 'Completed',
                'modal-row__value--warning': status === 'Pending',
                'modal-row__value--error': status === 'Failed',
              }"
            >
              {{ status }}
            </p>
          </div>
        </div>
      </template>
    </ViewTransactionModal>
  </section>
</template>

<script>
  import { mapState, mapActions } from "vuex";
  import ViewTransactionModal from "@/components/general/MediumModal.vue";
  import EmptyState from "@/components/notification/EmptyState.vue";
  import Pagination from "@/components/navigation/Pagination.vue";
  import {
    formatAmountToDollar,
    formatISOTime,
    formatISODate,
    removeUnderscore,
  } from "@/utils/helpers.js";

  export default {
    name: "WalletTransactionTable",

    components: {
      Pagination,
      ViewTransactionModal,
      EmptyState,
    },

    emits: ["gotoPage"],

    props: {
      table: { type: String, default: () => "" },
    },

    data() {
      return {
        loading: false,
        showViewTransactionModal: false,
        transactionRef: "",
        image: "",
        amount: "",
        type: "",
        date: "",
        status: "",
        availableStatuses: ["Pending", "Completed", "Failed"],
        tableHeaders: ["", "Details", "Reference", "Amount", "Status"],
        tableRows: [],
      };
    },

    computed: {
      ...mapState({
        transactions: (state) => state.organisationModule.walletTransactions,
        page: (state) => state.organisationModule.walletPage,
        pages: (state) => state.organisationModule.walletPages,
      }),
      gridStyling() {
        return {
          "grid-template-columns":
            "60px minmax(180px, 1fr) minmax(200px, 1fr) minmax(120px, auto) minmax(100px, auto)",
        };
      },
    },

    created() {
      this.fetchWalletTransactions();
    },

    methods: {
      ...mapActions("organisationModule", ["getWalletTransaction"]),

      viewTransaction(details) {
        this.transactionRef = details.reference.split(">")[1].split("<")[0];
        this.description = details.details.split(">")[1].split("<")[0];
        this.image = details.image.split(">")[1].split("<")[0];
        this.amount = details.amount;
        this.type = details.type.split(">")[1].split("<")[0];
        this.date = details.details.split(">")[4].split("<")[0];
        this.status = details.status;
        this.showViewTransactionModal = true;
      },

      async fetchWalletTransactions() {
        this.loading = true;
        const query = { page: 1, type: "cash", limit: 10 };
        await this.getWalletTransaction(query);
        this.loading = false;
      },

      updateTableRow(transactions) {
        this.tableRows = transactions.map((transaction) => {
          let type = removeUnderscore(transaction.type);
          switch (type) {
            case "Debit Sell Wallet":
              type = "Debit Wallet";
              break;

            case "Fund Sell Wallet":
              type = "Fund Wallet";
              break;
            default:
              break;
          }
          let name = `${transaction.token.name}`;
          switch (name) {
            case "GetEquity USD Token":
              name = "USD";
              break;
            default:
              break;
          }
          let volume = transaction.volume.toLocaleString();
          let details = `${type}: ${volume} ${name}`;
          let amount;
          if (transaction.price) {
            amount = transaction.price * transaction.volume;
          } else {
            amount = transaction.token?.price.buy * transaction.volume;
          }
          return {
            details: `<span class="tw-font-bold">${details}</span> <br />
          <span class="tw-text-black-light tw-text-xs">${formatISODate(
            transaction.createdAt
          )}  ${formatISOTime(transaction.createdAt)}</span>`,
            reference:
              type === "Fee" ||
              type === "Debit Wallet" ||
              type === "Send" ||
              type === "Withdraw" ||
              type === "Sell" ||
              type === "Plan"
                ? `<span class="tw-text-xs tw-mb-1">${transaction.reference}</span> <br />
          <span class="tw-text-xs tw-text-error">Debit</span>`
                : `<span class="tw-text-xs tw-mb-1">${transaction.reference}</span> <br />
          <span class="tw-text-xs tw-text-success">Credit</span>`,
            amount: formatAmountToDollar(amount, 2, transaction.token.currency ?? "USD"),
            status: transaction.status,
            type: `<span class="tw-hidden">${type}</span>`,
            image: `<span class="tw-hidden">${transaction.token?.image}</span>`,
          };
        });
      },

      async gotoPage(tablePage) {
        this.loading = true;
        this.tableRows = [];
        const query = { page: tablePage, type: "cash", limit: 10 };
        await this.getWalletTransaction(query);
        this.loading = false;
      },
    },

    watch: {
      transactions(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.updateTableRow(this.transactions);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .transaction-table-wrapper {
    @include fadeIn;
    padding-bottom: 24px;
  }

  .table-container {
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .transaction-table {
    width: 100%;
    min-width: max-content;
    border-collapse: collapse;

    @media (min-width: 1280px) {
      min-width: 100%;
    }

    &__head {
      background: #f8fafc;
    }

    &__header-row {
      display: grid;
      column-gap: 1.5rem;
      padding: 16px 20px;
      border-bottom: 1px solid #e2e8f0;
    }

    &__header-cell {
      font-family: "DM Sans", sans-serif;
      font-size: 13px;
      font-weight: 500;
      color: #64748b;
      text-align: left;
    }

    &__body {
      background: #ffffff;
    }

    &__row {
      display: grid;
      column-gap: 1.5rem;
      padding: 16px 20px;
      border-bottom: 1px solid #e2e8f0;
      align-items: center;
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background: #f8fafc;
      }

      &:last-child {
        border-bottom: none;
      }
    }

    &__icon {
      display: flex;
      justify-content: flex-end;
    }

    &__icon-box {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;

      &--credit {
        background: #ecfdf5;
        color: #059669;
      }

      &--debit {
        background: #fef2f2;
        color: #dc2626;
      }

      &--default {
        background: #f1f5f9;
        color: #64748b;
      }
    }

    &__cell {
      align-self: center;
      min-width: 0;
    }

    &__cell-text {
      font-family: "DM Sans", sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #0f172a;
      margin: 0;
      white-space: normal;
      overflow-wrap: break-word;
      word-break: break-word;

      :deep(span) {
        font-family: "DM Sans", sans-serif;
      }

      :deep(.tw-font-bold) {
        font-weight: 500;
      }

      :deep(.tw-text-black-light) {
        color: #64748b;
      }

      :deep(.tw-text-xs) {
        font-size: 12px;
      }

      :deep(.tw-text-error) {
        color: #dc2626;
      }

      :deep(.tw-text-success) {
        color: #059669;
      }
    }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 20px;
    font-family: "DM Sans", sans-serif;
    font-size: 12px;
    font-weight: 500;

    &--completed {
      background: #ecfdf5;
      color: #059669;
    }

    &--pending {
      background: #fef9c3;
      color: #ca8a04;
    }

    &--failed {
      background: #fef2f2;
      color: #dc2626;
    }
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    padding: 16px 20px 0;
  }

  .modal-header {
    margin-bottom: 24px;

    &__title {
      font-family: "DM Sans", sans-serif;
      font-size: 20px;
      font-weight: 500;
      color: #0f172a;
      margin: 0 0 8px 0;
    }

    &__ref {
      font-family: "DM Sans", sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748b;
      margin: 0;
    }
  }

  .modal-divider {
    height: 1px;
    background: #e2e8f0;
    margin-bottom: 20px;
  }

  .modal-row {
    display: flex;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid #e2e8f0;

    &:last-child {
      border-bottom: none;
    }

    &__item {
      &--right {
        text-align: right;
      }
    }

    &__label {
      font-family: "DM Sans", sans-serif;
      font-size: 12px;
      font-weight: 400;
      color: #64748b;
      margin: 0 0 4px 0;
    }

    &__value {
      font-family: "DM Sans", sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #0f172a;
      margin: 0;

      &--with-icon {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }

      &--success {
        color: #059669;
      }

      &--warning {
        color: #ca8a04;
      }

      &--error {
        color: #dc2626;
      }
    }
  }
</style>
